import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Globe, Search, Users, Check, Plus, X, Sparkles } from 'lucide-react';
import { supabase } from '../supabase';

interface ServerDiscoveryPanelProps {
  open: boolean;
  onClose: () => void;
  currentUserId?: string;
  // May itself perform the join (e.g. handleJoinVault, which writes
  // `vaults.members`) — awaited so the panel can refresh afterward.
  onJoinSuccess?: (vault: any) => void | Promise<void>;
}

export default function ServerDiscoveryPanel({
  open,
  onClose,
  currentUserId,
  onJoinSuccess,
}: ServerDiscoveryPanelProps) {
  const [publicServers, setPublicServers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    fetchPublicServers();
  }, [open, currentUserId]);

  const fetchPublicServers = async () => {
    setLoading(true);
    // There's no `vault_members` join table in this schema — membership
    // lives entirely on `vaults.members` (a JSONB array of user ids; see
    // the RLS audit's documented gap in 20260719000000_p1_11_rls_audit.sql).
    // The old modal this panel replaced queried/upserted a `vault_members`
    // table that never existed — Supabase's client doesn't throw on a
    // missing-relation error, so every "join" silently no-op'd against the
    // database while the UI optimistically showed a checkmark anyway.
    // Fetch only public vaults with essential columns to reduce bandwidth
    const { data: publicList } = await supabase
      .from('vaults')
      .select('id, name, icon_url, description, members, type')
      .eq('type', 'public')
      .order('created_at', { ascending: false })
      .limit(100);
    setPublicServers(publicList || []);
    setLoading(false);
  };

  const handleJoinServer = async (vault: any) => {
    if (!currentUserId) return;
    setJoiningId(vault.id);
    try {
      await onJoinSuccess?.(vault);
      // Refresh so `vault.members` reflects the write onJoinSuccess just
      // made, flipping this card to "Joined" if the panel stays open.
      await fetchPublicServers();
    } catch (e) {
      console.error('Error joining server:', e);
    } finally {
      setJoiningId(null);
    }
  };

  const filtered = publicServers.filter(v => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      (v.name || '').toLowerCase().includes(q) ||
      (v.description || '').toLowerCase().includes(q)
    );
  });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
          transition={{ duration: 0.18 }}
          className="w-full sm:w-[380px] lg:w-[420px] shrink-0 h-full bg-[#0b0e1a]/95 border-l border-white/10 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#0b0e1a] to-[#131830] shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-[var(--neon)]/10 border border-[var(--neon)]/20 flex items-center justify-center text-[var(--neon)] shrink-0">
                <Globe size={16} />
              </div>
              <div className="min-w-0">
                <h2 className="text-white font-black text-xs uppercase tracking-widest flex items-center gap-1.5 truncate">
                  Discover Servers <Sparkles size={12} className="text-[var(--neon)] shrink-0" />
                </h2>
                <p className="text-[9px] text-white/40 uppercase tracking-widest mt-0.5 truncate">Join active PsydeChat servers</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all shrink-0"
              title="Close discovery panel"
            >
              <X size={16} />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-3 border-b border-white/5 bg-black/20 shrink-0">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                placeholder="Search public servers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-xs text-white outline-none focus:border-[var(--neon)] transition-all"
              />
            </div>
          </div>

          {/* Servers List */}
          <div className="flex-1 overflow-y-auto no-scrollbar p-3">
            {loading ? (
              <div className="flex items-center justify-center h-full text-white/30 text-[10px] font-mono uppercase tracking-widest">
                Scanning Server Directory...
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-white/30 text-[10px] uppercase tracking-widest gap-2">
                <Globe size={28} className="text-white/20" />
                <span>No public servers found</span>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {filtered.map(vault => {
                  const isJoined = (vault.members || []).includes(currentUserId) || vault.created_by === currentUserId;
                  const isJoining = joiningId === vault.id;

                  return (
                    <div
                      key={vault.id}
                      className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-all flex flex-col gap-3 group"
                    >
                      <div className="flex gap-3 items-start">
                        <img
                          src={vault.icon_url || '/psydechat-icon-no-text.png'}
                          alt=""
                          className="w-11 h-11 rounded-xl object-cover border border-white/10 shrink-0 shadow-lg group-hover:scale-105 transition-transform"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-black text-xs uppercase tracking-tight truncate group-hover:text-[var(--neon)] transition-colors">
                            {vault.name}
                          </h3>
                          <p className="text-[11px] text-white/50 line-clamp-2 mt-0.5 font-sans leading-relaxed">
                            {vault.description || 'Welcome to our public PsydeChat server community!'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2.5 border-t border-white/5">
                        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase text-white/40 tracking-wider">
                          <Users size={11} className="text-[var(--neon)]" />
                          <span>{vault.members?.length || 0} Members</span>
                        </div>

                        {isJoined ? (
                          <div className="px-3 py-1.5 rounded-xl bg-white/10 text-white/60 font-black text-[9px] uppercase tracking-widest flex items-center gap-1 cursor-default">
                            <Check size={12} className="text-emerald-400" /> Joined
                          </div>
                        ) : (
                          <button
                            onClick={() => handleJoinServer(vault)}
                            disabled={isJoining}
                            className="px-3.5 py-1.5 rounded-xl bg-[var(--neon)] text-black font-black text-[9px] uppercase tracking-widest hover:scale-105 transition-all shadow-[0_0_15px_var(--neon)] flex items-center gap-1 cursor-pointer disabled:opacity-40"
                          >
                            <Plus size={12} /> {isJoining ? 'Joining...' : 'Join'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
